import { Injectable, inject } from '@angular/core';

import {
  BigNumberPointSelection,
  BreakdownPointSelection,
  CrosstabPointSelection,
  DonutPointSelection,
  GaugePointSelection,
  ImpactPointSelection,
  NmotPointSelection,
  ScatterPlotPointSelection,
  SentimentPolarityPointSelection,
  SentimentScorePointSelection,
  SentimentVsVolumePointSelection,
  VolumeOverTimePointSelection,
  VolumePointSelection,
  WidgetPointSelection,
} from '.';
import { WidgetDataSource } from '../../../../../../../models';
import { Widget, WidgetGroup } from '../../../../../../../providers/bridge-api/dashboard.service';
import { MatchmakerHandler } from '../../../../../../../providers/semantria-api/spote.service';
import { AiInsightPayload } from '../../../../../../../providers/storage-api/ai-insights-dataprep.service';
import { FieldService } from '../../../../../../../providers/storage-api/field.service';
import { EnableClassTelemetry } from '../../../../../../../services/otel.service';
import { ChartOptionUtils } from '../../../../../../../utils/chart-options.utils';
import { FilterService } from '../../../../../../collection/services/filter.service';
import { ComparisonHandlerService } from '../../../../../../dashboard/services/comparison-handler-service';
import { WidgetChartOptions } from '../../../../widget-chart/widget-chart.component';
import { WidgetAiInsightsSummaryService } from '../summary/widget-ai-insights-summary.service';
import {
  NumericRange,
  WidgetAiInsightsValidatorService,
} from '../validation/widget-ai-insights-validator.service';

export type AiInsightPayloadParams = Omit<AiInsightPayload, 'scene_id'>;

@EnableClassTelemetry()
@Injectable()
export class WidgetAiInsightsPointSelectionService {
  private comparisonHandlerService = inject(ComparisonHandlerService);
  private filterService = inject(FilterService);
  private summaryService = inject(WidgetAiInsightsSummaryService);
  private insightValidatorService = inject(WidgetAiInsightsValidatorService);

  private widgetPointSelection: WidgetPointSelection;

  constructor() {
    this.widgetPointSelection = this.buildWidgetPointSelection();
  }

  checkSelectionValidForHandler(
    selectedPoints: any[],
    handler: MatchmakerHandler,
    _widget: Widget
  ) {
    if (!handler) {
      return false;
    }

    const clickablePoints = this.getClickablePoints(handler);

    return (
      clickablePoints.min <= selectedPoints.length && selectedPoints.length <= clickablePoints.max
    );
  }

  assembleHandlerParameters(
    selectedPoints: any[],
    handler: MatchmakerHandler,
    widget: Widget<WidgetChartOptions>,
    dataSource: WidgetDataSource,
    contentChannel: string,
    widgetGroup?: WidgetGroup
  ): AiInsightPayloadParams {
    this.widgetPointSelection = this.buildWidgetPointSelection(widget);

    const comparison = this.comparisonHandlerService.getComparison(widget.payload.comparison);
    const sectionFilter = this.calculateSectionFilter(handler, dataSource);

    const params = this.widgetPointSelection.assemblePayloadFromSelection(
      selectedPoints,
      widget as Widget<WidgetChartOptions>,
      dataSource,
      widgetGroup,
      comparison,
      sectionFilter
    );

    params.properties.summary = this.summaryService.generateInsightSummary(selectedPoints, widget);

    if (contentChannel) {
      params.scene_params.content_channel = contentChannel;
    }

    return params;
  }

  private calculateSectionFilter(handler: MatchmakerHandler, dataSource: WidgetDataSource) {
    const sectionFilter = this.insightValidatorService.getHandlerProperty(
      handler,
      'SECTION_DATA',
      true
    );
    if (!sectionFilter || ['TRUE', 'FALSE', 'ALL'].includes(sectionFilter.toLocaleUpperCase())) {
      return;
    }

    const sectionFilterKeyValue = sectionFilter.split(':');
    const fields = dataSource.sectionFieldNames.map((f) => ({
      id: f,
      name: FieldService.getFieldLongName(f),
    }));
    const sectionField = fields.find(
      (f) =>
        f.name.localeCompare(sectionFilterKeyValue[0], undefined, { sensitivity: 'accent' }) === 0
    );

    return [
      {
        field: sectionField.id,
        buckets: sectionFilterKeyValue[1].split(','),
      },
    ];
  }

  private buildWidgetPointSelection(widget?) {
    if (!widget) {
      return new WidgetPointSelection(this.filterService);
    }

    switch (widget.payload.type) {
      case 'donut':
        return new DonutPointSelection(this.filterService);
      case 'numericMetadata':
        return new NmotPointSelection(this.filterService);
      case 'breakdown':
        return new BreakdownPointSelection(this.filterService);
      case 'gauge':
        return new GaugePointSelection(this.filterService);
      case 'bigNumber':
        return new BigNumberPointSelection(this.filterService);
      case 'sentimentPolarity':
        return new SentimentPolarityPointSelection(this.filterService);
      case 'sentimentScore':
        return new SentimentScorePointSelection(this.filterService);
      case 'documentCount':
        if (ChartOptionUtils.isOverTimeAggregator(widget.payload.aggregator)) {
          return new VolumeOverTimePointSelection(this.filterService);
        }
        return new VolumePointSelection(this.filterService);
      case 'scatterPlot':
        return new ScatterPlotPointSelection(this.filterService);
      case 'sentimentVolume':
        return new SentimentVsVolumePointSelection(this.filterService);
      case 'impact':
        return new ImpactPointSelection(this.filterService);
      case 'crosstab':
        return new CrosstabPointSelection(this.filterService);
      default:
        return new WidgetPointSelection(this.filterService);
    }
  }

  getClickablePoints(handler: MatchmakerHandler): NumericRange {
    return this.insightValidatorService.getClickablePoints(handler);
  }
}
